import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ChevronDown,
  ChevronRight,
  TrendingDown,
  TrendingUp,
  FileText,
  Calculator,
  Users,
  Building,
  DollarSign,
  Info,
  ExternalLink,
  BookOpen
} from 'lucide-react';

const CBADisplay = ({ cbaData, onRegenerateWithContext }) => {
  const [expandedCategories, setExpandedCategories] = useState({});
  const [expandedItems, setExpandedItems] = useState({});
  const [showMethodology, setShowMethodology] = useState(false);

  // Group costs and benefits into categories
  const groupByCategory = (items) => {
    const grouped = {};
    items.forEach(item => {
      const category = item.mainCategory || item.category;
      if (!grouped[category]) {
        grouped[category] = {
          name: category,
          items: [],
          total: 0
        };
      }
      grouped[category].items.push(item);
      grouped[category].total += item.amount;
    });
    return grouped;
  };

  const costCategories = groupByCategory(cbaData.costs || []);
  const benefitCategories = groupByCategory(cbaData.benefits || []);

  const toggleCategory = (type, category) => {
    const key = `${type}-${category}`;
    setExpandedCategories(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const toggleItem = (type, category, index) => {
    const key = `${type}-${category}-${index}`;
    setExpandedItems(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const getCategoryIcon = (category) => {
    if (category.includes('Compliance')) return FileText;
    if (category.includes('Administrative')) return Building;
    if (category.includes('Training')) return Users;
    if (category.includes('Equipment')) return Calculator;
    if (category.includes('Economic')) return TrendingUp;
    if (category.includes('Public')) return Users;
    return DollarSign;
  };

  const renderCategorySection = (categories, type, color) => {
    return Object.entries(categories).map(([categoryName, categoryData]) => {
      const isExpanded = expandedCategories[`${type}-${categoryName}`];
      const CategoryIcon = getCategoryIcon(categoryName);
      
      return (
        <motion.div
          key={categoryName}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          style={{
            background: 'white',
            borderRadius: '12px',
            border: '1px solid #e5e7eb',
            marginBottom: '16px',
            overflow: 'hidden',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
          }}
        >
          {/* Category Header */}
          <div
            style={{
              padding: '20px',
              cursor: 'pointer',
              background: isExpanded ? '#f9fafb' : 'white',
              transition: 'all 0.2s ease'
            }}
            onClick={() => toggleCategory(type, categoryName)}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  background: `${color}10`,
                  borderRadius: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <CategoryIcon style={{ width: '20px', height: '20px', color }} />
                </div>
                <div>
                  <h4 style={{
                    fontSize: '16px',
                    fontWeight: '600',
                    color: '#111827',
                    marginBottom: '4px'
                  }}>
                    {categoryName}
                  </h4>
                  <p style={{
                    fontSize: '12px',
                    color: '#6b7280'
                  }}>
                    {categoryData.items.length} item{categoryData.items.length !== 1 ? 's' : ''}
                  </p>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <span style={{
                  background: `${color}15`,
                  padding: '6px 16px',
                  borderRadius: '20px',
                  fontSize: '16px',
                  fontWeight: '700',
                  color
                }}>
                  ${categoryData.total.toLocaleString()}
                </span>
                {isExpanded ? (
                  <ChevronDown style={{ width: '20px', height: '20px', color: '#6b7280' }} />
                ) : (
                  <ChevronRight style={{ width: '20px', height: '20px', color: '#6b7280' }} />
                )}
              </div>
            </div>
          </div>

          {/* Category Items */}
          <AnimatePresence>
            {isExpanded && (
              <motion.div
                initial={{ height: 0 }}
                animate={{ height: 'auto' }}
                exit={{ height: 0 }}
                style={{
                  borderTop: '1px solid #e5e7eb',
                  background: '#fafafa'
                }}
              >
                <div style={{ padding: '16px' }}>
                  {categoryData.items.map((item, index) => {
                    const itemKey = `${type}-${categoryName}-${index}`;
                    const isItemExpanded = expandedItems[itemKey];
                    
                    return (
                      <div
                        key={index}
                        style={{
                          background: 'white',
                          borderRadius: '8px',
                          padding: '12px',
                          marginBottom: index < categoryData.items.length - 1 ? '8px' : 0,
                          border: '1px solid #e5e7eb',
                          cursor: 'pointer'
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleItem(type, categoryName, index);
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <div style={{ flex: 1 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                              {isItemExpanded ? (
                                <ChevronDown style={{ width: '14px', height: '14px', color: '#6b7280' }} />
                              ) : (
                                <ChevronRight style={{ width: '14px', height: '14px', color: '#6b7280' }} />
                              )}
                              <p style={{ fontSize: '14px', fontWeight: '500', color: '#374151' }}>
                                {item.description || item.category}
                              </p>
                            </div>
                          </div>
                          <span style={{
                            background: `${color}10`,
                            padding: '4px 10px',
                            borderRadius: '16px',
                            fontSize: '13px',
                            fontWeight: '600',
                            color
                          }}>
                            ${item.amount.toLocaleString()}
                          </span>
                        </div>

                        {/* Expanded Item Details */}
                        <AnimatePresence>
                          {isItemExpanded && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: 'auto', opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              style={{
                                marginTop: '12px',
                                paddingTop: '12px',
                                borderTop: '1px solid #f3f4f6'
                              }}
                            >
                              {/* Methodology */}
                              <div style={{
                                background: '#f9fafb',
                                padding: '12px',
                                borderRadius: '6px',
                                marginBottom: '8px'
                              }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}>
                                  <Calculator style={{ width: '14px', height: '14px', color: '#6b7280' }} />
                                  <span style={{ fontSize: '12px', fontWeight: '600', color: '#4b5563' }}>
                                    Calculation Methodology
                                  </span>
                                </div>
                                <div style={{ fontSize: '12px', lineHeight: '1.6', color: '#6b7280', whiteSpace: 'pre-wrap' }}>
                                  {item.methodology && item.methodology !== item.description ? 
                                    item.methodology : 
                                    'Based on regulatory impact analysis'}
                                </div>
                              </div>

                              {/* Sources */}
                              {item.sources && (
                                <div style={{
                                  background: '#f9fafb',
                                  padding: '12px',
                                  borderRadius: '6px',
                                  marginBottom: '8px'
                                }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}>
                                    <BookOpen style={{ width: '14px', height: '14px', color: '#6b7280' }} />
                                    <span style={{ fontSize: '12px', fontWeight: '600', color: '#4b5563' }}>
                                      Data Sources
                                    </span>
                                  </div>
                                  <ul style={{ margin: 0, paddingLeft: '16px' }}>
                                    {item.sources.map((source, idx) => (
                                      <li key={idx} style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>
                                        {source.url ? (
                                          <a href={source.url} target="_blank" rel="noopener noreferrer" 
                                             style={{ color: '#2563eb', textDecoration: 'none' }}>
                                            {source.name} <ExternalLink style={{ width: '10px', height: '10px', display: 'inline' }} />
                                          </a>
                                        ) : source.name || source}
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}

                              {/* Assumptions */}
                              {item.assumptions && (
                                <div style={{
                                  background: '#fef3c7',
                                  padding: '12px',
                                  borderRadius: '6px'
                                }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}>
                                    <Info style={{ width: '14px', height: '14px', color: '#92400e' }} />
                                    <span style={{ fontSize: '12px', fontWeight: '600', color: '#92400e' }}>
                                      Key Assumptions
                                    </span>
                                  </div>
                                  <p style={{ fontSize: '12px', lineHeight: '1.6', color: '#92400e' }}>
                                    {item.assumptions}
                                  </p>
                                </div>
                              )}
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    );
                  })}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      );
    });
  };

  return (
    <div style={{ padding: '24px' }}>
      {/* Summary Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px', marginBottom: '32px' }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          style={{
            background: 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)',
            padding: '24px',
            borderRadius: '12px',
            border: '1px solid rgba(239, 68, 68, 0.1)'
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
            <span style={{ fontSize: '14px', fontWeight: '600', color: '#991b1b' }}>Total Costs</span>
            <TrendingDown style={{ width: '20px', height: '20px', color: '#dc2626' }} />
          </div>
          <p style={{ fontSize: '32px', fontWeight: '700', color: '#dc2626' }}>
            ${cbaData.summary.total_costs.toLocaleString()}
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          style={{
            background: 'linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)',
            padding: '24px',
            borderRadius: '12px',
            border: '1px solid rgba(34, 197, 94, 0.1)'
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
            <span style={{ fontSize: '14px', fontWeight: '600', color: '#14532d' }}>Total Benefits</span>
            <TrendingUp style={{ width: '20px', height: '20px', color: '#16a34a' }} />
          </div>
          <p style={{ fontSize: '32px', fontWeight: '700', color: '#16a34a' }}>
            ${cbaData.summary.total_benefits.toLocaleString()}
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          style={{
            background: cbaData.summary.net_benefit >= 0
              ? 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)'
              : 'linear-gradient(135deg, #fef2f2 0%, #fecaca 100%)',
            padding: '24px',
            borderRadius: '12px',
            border: `1px solid ${cbaData.summary.net_benefit >= 0 ? 'rgba(59, 130, 246, 0.1)' : 'rgba(239, 68, 68, 0.1)'}`
          }}
        >
          <div style={{ marginBottom: '8px' }}>
            <span style={{ fontSize: '14px', fontWeight: '600', color: '#374151' }}>Net Benefit</span>
            <span style={{
              marginLeft: '12px',
              background: 'white',
              padding: '4px 8px',
              borderRadius: '12px',
              fontSize: '12px',
              fontWeight: '600',
              color: '#1e40af'
            }}>
              B/C: {cbaData.summary.benefit_cost_ratio}
            </span>
          </div>
          <p style={{
            fontSize: '32px',
            fontWeight: '700',
            color: cbaData.summary.net_benefit >= 0 ? '#2563eb' : '#ef4444'
          }}>
            ${cbaData.summary.net_benefit.toLocaleString()}
          </p>
        </motion.div>
      </div>

      {/* Cost Categories */}
      <div style={{ marginBottom: '32px' }}>
        <h3 style={{
          fontSize: '18px',
          fontWeight: '700',
          color: '#1f2937',
          marginBottom: '16px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <TrendingDown style={{ width: '20px', height: '20px', color: '#dc2626' }} />
          Cost Analysis
        </h3>
        {renderCategorySection(costCategories, 'cost', '#dc2626')}
      </div>

      {/* Benefit Categories */}
      <div style={{ marginBottom: '32px' }}>
        <h3 style={{
          fontSize: '18px',
          fontWeight: '700',
          color: '#1f2937',
          marginBottom: '16px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <TrendingUp style={{ width: '20px', height: '20px', color: '#16a34a' }} />
          Benefit Analysis
        </h3>
        {renderCategorySection(benefitCategories, 'benefit', '#16a34a')}
      </div>

      {/* Overall Methodology */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5 }}
        style={{
          background: 'linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%)',
          padding: '24px',
          borderRadius: '12px',
          marginTop: '32px'
        }}
      >
        <button
          onClick={() => setShowMethodology(!showMethodology)}
          style={{
            background: 'none',
            border: 'none',
            padding: 0,
            cursor: 'pointer',
            width: '100%',
            textAlign: 'left',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <h3 style={{
            fontSize: '16px',
            fontWeight: '700',
            color: '#1f2937',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <Info style={{ width: '18px', height: '18px' }} />
            Analysis Methodology & Sources
          </h3>
          {showMethodology ? (
            <ChevronDown style={{ width: '20px', height: '20px', color: '#6b7280' }} />
          ) : (
            <ChevronRight style={{ width: '20px', height: '20px', color: '#6b7280' }} />
          )}
        </button>
        
        <AnimatePresence>
          {showMethodology && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              style={{
                marginTop: '16px',
                paddingTop: '16px',
                borderTop: '1px solid #d1d5db'
              }}
            >
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div>
                  <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>
                    Analysis Parameters
                  </h4>
                  <ul style={{ fontSize: '13px', color: '#6b7280', lineHeight: '1.6' }}>
                    <li>Timeframe: {cbaData.timeframe}</li>
                    <li>Discount Rate: {cbaData.discount_rate}</li>
                    <li>Present Value Method: Net Present Value (NPV)</li>
                    <li>Currency: USD (current year dollars)</li>
                  </ul>
                </div>
                <div>
                  <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>
                    Primary Data Sources
                  </h4>
                  <ul style={{ fontSize: '13px', color: '#6b7280', lineHeight: '1.6' }}>
                    <li>Virginia Regulatory Town Hall</li>
                    <li>Virginia Economic Impact Analysis</li>
                    <li>Federal regulatory cost databases</li>
                    <li>Industry compliance cost surveys</li>
                  </ul>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
};

export default CBADisplay;